import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Camera, MapPin, Clock, Globe, Save } from 'lucide-react';
import API from '../services/api';

const ProfilPro = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const photoRef = useRef(null);
  const banniereRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [profil, setProfil] = useState(null);
  const [form, setForm] = useState({
    description_pro: '',
    adresse: '',
    horaires: '',
    facebook: '',
    instagram: '',
    site_web: ''
  });

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    if (!['vendeur_pro', 'admin'].includes(user.role)) {
      navigate('/abonnements');
      return;
    }

    fetch(`${config.API_URL}/api/auth/profil`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.json())
      .then(p => {
        setProfil(p);
        setForm({
          description_pro: p.description_pro || '',
          adresse: p.adresse || '',
          horaires: p.horaires || '',
          facebook: p.facebook || '',
          instagram: p.instagram || '',
          site_web: p.site_web || ''
        });
      });
  }, [user?.id, navigate]);

  const handleSauvegarder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.put('/auth/profil-pro', form);
      setMessage('✅ Profil mis à jour avec succès !');
    } catch (err) {
      setMessage('❌ Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoProfil = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('photo', file);
    try {
      const res = await fetch(`${config.API_URL}/api/auth/photo-profil`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      setProfil({ ...profil, photo_profil: data.url });
      setMessage('✅ Photo de profil mise à jour !');
    } catch (err) {
      setMessage('❌ Erreur upload photo');
    }
  };

  const handleBanniere = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('banniere', file);
    try {
      const res = await fetch(`${config.API_URL}/api/auth/banniere`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await res.json();
      setProfil({ ...profil, banniere: data.url });
      setMessage('✅ Bannière mise à jour !');
    } catch (err) {
      setMessage('❌ Erreur upload bannière');
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-yellow-400 transition bg-white";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-white text-2xl font-black">Personnaliser ma vitrine</h1>
          <p style={{ color: '#F5A623' }} className="text-sm mt-1">
            Rendez votre profil professionnel attractif
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {message && (
          <div className={`p-4 rounded-2xl mb-6 text-sm font-semibold ${
            message.startsWith('✅')
              ? 'bg-green-50 border border-green-200 text-green-600'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}>
            {message}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100 mb-6">
          <div style={{
            height: '180px',
            background: profil?.banniere
              ? `url(${getImageUrl(profil.banniere)}) center/cover`
              : 'linear-gradient(135deg, #0A0A0A, #1a1a1a)',
            position: 'relative'
          }}>
            <button
              onClick={() => banniereRef.current.click()}
              style={{ background: '#F5A623' }}
              className="absolute bottom-3 right-3 text-black px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 hover:opacity-90 transition"
            >
              <Camera size={14} /> Changer la bannière
            </button>
            <input ref={banniereRef} type="file" accept="image/*" onChange={handleBanniere} className="hidden" />
          </div>

          <div className="px-6 pb-6">
            <div className="relative inline-block -mt-12 mb-4">
              <div
                style={{ background: '#F5A623', border: '4px solid white' }}
                className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden"
              >
                {profil?.photo_profil ? (
                  <img src={getImageUrl(profil.photo_profil)} alt="profil" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-black font-black text-2xl">
                    {user?.prenom?.[0]}{user?.nom?.[0]}
                  </span>
                )}
              </div>
              <button
                onClick={() => photoRef.current.click()}
                style={{ background: '#0A0A0A' }}
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition"
              >
                <Camera size={14} className="text-white" />
              </button>
              <input ref={photoRef} type="file" accept="image/*" onChange={handlePhotoProfil} className="hidden" />
            </div>
            <h2 className="font-black text-gray-900 text-xl">{user?.prenom} {user?.nom}</h2>
            <p className="text-gray-400 text-sm">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSauvegarder} className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Présentation
            </h2>
            <div>
              <label className={labelClass}>Description de votre activité</label>
              <textarea
                value={form.description_pro}
                onChange={(e) => setForm({ ...form, description_pro: e.target.value })}
                rows={4}
                className={inputClass}
                placeholder="Décrivez votre activité, vos spécialités, votre expérience..."
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Coordonnées
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}><MapPin size={12} className="inline mr-1" /> Adresse</label>
                <input
                  type="text"
                  value={form.adresse}
                  onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                  className={inputClass}
                  placeholder="123 Rue du Commerce, Brazzaville"
                />
              </div>
              <div>
                <label className={labelClass}><Clock size={12} className="inline mr-1" /> Horaires d'ouverture</label>
                <input
                  type="text"
                  value={form.horaires}
                  onChange={(e) => setForm({ ...form, horaires: e.target.value })}
                  className={inputClass}
                  placeholder="Lun-Ven: 8h-18h, Sam: 9h-13h"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Réseaux sociaux & Web
            </h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>📘 Facebook</label>
                <input
                  type="text"
                  value={form.facebook}
                  onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                  className={inputClass}
                  placeholder="https://facebook.com"
                />
              </div>
              <div>
                <label className={labelClass}>📷 Instagram</label>
                <input
                  type="text"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  className={inputClass}
                  placeholder="https://instagram.com"
                />
              </div>
              <div>
                <label className={labelClass}><Globe size={12} className="inline mr-1" /> Site web</label>
                <input
                  type="url"
                  value={form.site_web}
                  onChange={(e) => setForm({ ...form, site_web: e.target.value })}
                  className={inputClass}
                  placeholder="https://votresite.com"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ background: '#F5A623' }}
            className="w-full text-black py-3 rounded-2xl font-black hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save size={18} />
            {loading ? 'Sauvegarde...' : 'Sauvegarder le profil'}
          </button>

          <div className="text-center">
            <a
              href={`/vitrine/${user?.id}`}
              style={{ color: '#F5A623' }}
              className="text-sm font-semibold hover:opacity-80 transition"
            >
              👁 Voir ma vitrine publique →
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilPro;